import { Components, chipClasses as MuiChipClasses, Theme } from '@mui/material';
import { grey } from '../../theme/palette/colors';
import { chipClasses } from './Chip';

export const CHIP_SMALL_HEIGHT = '20px';

export const MuiChip: Components<Theme>['MuiChip'] = {
  defaultProps: {
    variant: 'filled',
    size: 'small',
  },
  styleOverrides: {
    root: ({ theme }) => ({
      borderRadius: theme.shape.inlineRadius,
    }),
    sizeSmall: {
      height: CHIP_SMALL_HEIGHT,
    },
    deleteIcon: ({ theme }) => ({
      padding: '2px',
      color: theme.palette.text.legacySecondary,
    }),
    icon: ({ theme }) => ({
      color: theme.palette.text.primary,
      width: '12px',
      height: '12px',
    }),
    iconSmall: ({ theme }) => ({
      marginLeft: theme.spacing(1),
    }),
    labelSmall: {
      fontSize: '12px',
    },
    // Represents default static chips, clickable chips, and selected selectable chips
    filled: ({ theme }) => {
      if (theme.featureFlags.panwRebrandStubYear) {
        return {
          backgroundColor: theme.palette.interactive.neutral.default,
          border: `1px solid ${theme.palette.border.bold}`,
          color: theme.palette.text.primary,
          '&:hover, &:focus': {
            backgroundColor: theme.palette.interactive.neutral.hover,
          },
          [`&.${MuiChipClasses.disabled}`]: {
            opacity: 1,
            color: theme.palette.text.disabled,
            [`& .${MuiChipClasses.deleteIcon}`]: {
              color: theme.palette.text.disabled,
            },
            [`& .${MuiChipClasses.icon}`]: {
              color: theme.palette.text.disabled,
            },
          },
          // Selectable chip in selected state
          [`&.${chipClasses.selectable}`]: {
            backgroundColor: theme.palette.interactive.highlight.default,
            border: `1px solid ${theme.palette.border.bold}`,
            color: theme.palette.text.inversePrimary,
            '&:hover, &:focus': {
              backgroundColor: theme.palette.interactive.highlight.hover,
            },
            [`& .${MuiChipClasses.icon}`]: {
              color: theme.palette.text.inversePrimary,
            },
            [`&.${MuiChipClasses.disabled}`]: {
              opacity: 1,
              backgroundColor: theme.palette.interactive.neutral.default,
              color: theme.palette.text.disabled,
              [`& .${MuiChipClasses.icon}`]: {
                color: theme.palette.text.disabled,
              },
            },
          },
        };
      }

      const darkModeBackgroundColor =
        theme.palette.mode === 'dark' ? theme.palette.grey[300] : theme.palette.status.default.lowContrast;

      return {
        backgroundColor: darkModeBackgroundColor,
        border: `1px solid ${darkModeBackgroundColor}`,
        color: theme.palette.text.primary,
        [`& .${MuiChipClasses.icon}`]: {
          color: theme.palette.text.primary,
        },
      };
    },
    // Represents chips that are selectable but are not currently selected
    outlined: ({ theme }) => {
      if (theme.featureFlags.panwRebrandStubYear) {
        return {
          backgroundColor: 'transparent',
          border: `1px solid ${theme.palette.border.bold}`,
          color: theme.palette.text.primary,
          '&:hover, &:focus': {
            backgroundColor: theme.palette.interactive.neutral.hover,
          },
          [`& .${MuiChipClasses.icon}`]: {
            color: theme.palette.text.primary,
          },
          [`&.${MuiChipClasses.disabled}`]: {
            opacity: 1,
            border: `1px solid ${theme.palette.border.default}`,
            color: theme.palette.text.disabled,
            [`& .${MuiChipClasses.icon}`]: {
              color: theme.palette.text.disabled,
            },
          },
        };
      }

      const isLight = theme.palette.mode === 'light';
      return {
        backgroundColor: theme.palette.background.container,
        border: `1px solid ${isLight ? grey[150] : grey[850]}`,
        color: theme.palette.text.legacySecondary,
        '&:hover, &:focus': {
          backgroundColor: isLight ? grey[50] : grey[800],
        },
        [`& .${MuiChipClasses.icon}`]: {
          color: theme.palette.text.legacySecondary,
        },
      };
    },
  },
};
